import { z } from 'zod';

/**
 * The ten rarities, ordered from lowest to highest tier.
 *
 * The array order IS the tier order. Never reorder it: persisted rows, drop
 * tables and UI sorting all derive from this list.
 */
export const RARITIES = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'mythic',
  'legendary',
  'excellent',
  'perfect',
  'anomaly',
  'prismatic',
] as const;

export type Rarity = (typeof RARITIES)[number];

export const raritySchema = z.enum(RARITIES);

/** 1-based tier. `common` = 1, `prismatic` = 10. */
export function rarityTier(rarity: Rarity): number {
  return RARITIES.indexOf(rarity) + 1;
}

/**
 * How a rarity paints its border / glow.
 *
 * - `solid`    - one dominant neon colour with a lighter shimmer partner.
 * - `dual`     - a warm gradient that circulates (Anomaly: yellow -> red).
 * - `spectrum` - full rainbow cycle (Prismatic).
 */
export type RarityPaintKind = 'solid' | 'dual' | 'spectrum';

export interface RarityTheme {
  readonly id: Rarity;
  /** English display label. Localisation happens in the UI layer. */
  readonly label: string;
  readonly tier: number;
  readonly paint: RarityPaintKind;
  /**
   * Ordered colour stops, and the ONLY place a rarity colour is written down.
   *
   * Stop 0 is the dominant colour - the one the border itself is painted with
   * and the one a badge borrows. The stops after it are the poles the animated
   * ring sweeps between, which is why even a `solid` rarity has two: a single
   * flat colour cannot shimmer, and a rarity that cannot shimmer reads as dead
   * next to one that does.
   */
  readonly colors: readonly string[];
  /**
   * Relative intensity of neon and animation, 0..1, ramping with the tier.
   *
   * The UI multiplies halo radius and animation amplitude by this, which is
   * what makes the presentation escalate with rarity without a per-rarity
   * table of magic numbers anywhere in CSS.
   */
  readonly intensity: number;
}

function intensityForTier(tier: number): number {
  // Linear ramp across the ten tiers. Deliberately simple and tweakable.
  return Number((tier / RARITIES.length).toFixed(2));
}

export const RARITY_THEME: Readonly<Record<Rarity, RarityTheme>> = Object.freeze({
  common: {
    id: 'common',
    label: 'Common',
    tier: 1,
    paint: 'solid',
    // Two near-identical whites: bright and clean, with no movement to speak
    // of. The lowest tier should read as solid, not as a light show.
    colors: ['#F2F4F8', '#FFFFFF'],
    intensity: intensityForTier(1),
  },
  uncommon: {
    id: 'uncommon',
    label: 'Uncommon',
    tier: 2,
    paint: 'solid',
    colors: ['#3DFF7A', '#8CFFB4'],
    intensity: intensityForTier(2),
  },
  rare: {
    id: 'rare',
    label: 'Rare',
    tier: 3,
    paint: 'solid',
    colors: ['#3B82F6', '#7CB8FF'],
    intensity: intensityForTier(3),
  },
  epic: {
    id: 'epic',
    label: 'Epic',
    tier: 4,
    paint: 'solid',
    colors: ['#A855F7', '#DDA0FF'],
    intensity: intensityForTier(4),
  },
  mythic: {
    id: 'mythic',
    label: 'Mythic',
    tier: 5,
    paint: 'solid',
    // The second pole runs warm rather than pale: the pulse should feel hot.
    colors: ['#FF2E4D', '#FF8A5C'],
    intensity: intensityForTier(5),
  },
  legendary: {
    id: 'legendary',
    label: 'Legendary',
    tier: 6,
    paint: 'solid',
    colors: ['#FFC530', '#FFF3A0'],
    intensity: intensityForTier(6),
  },
  excellent: {
    id: 'excellent',
    label: 'Excellent',
    tier: 7,
    paint: 'solid',
    // Deep green to vivid emerald - the widest swing of any solid rarity, and
    // the reason the sweep is visible rather than merely present.
    colors: ['#00B85C', '#2BFFA6'],
    intensity: intensityForTier(7),
  },
  perfect: {
    id: 'perfect',
    label: 'Perfect',
    tier: 8,
    paint: 'solid',
    // Turquoise to blue: the alternation is between two hues, not two
    // brightnesses, which is what makes it read as a different rarity.
    colors: ['#22E4DB', '#3B9DFF'],
    intensity: intensityForTier(8),
  },
  anomaly: {
    id: 'anomaly',
    label: 'Anomaly',
    tier: 9,
    paint: 'dual',
    // Yellow, gold, orange, red - and deliberately nothing else. Anomaly's
    // identity is that it burns; a green or blue stop anywhere in here would
    // make it read as a lesser Prismatic.
    colors: ['#FFE066', '#FFB020', '#FF6A00', '#FF1F1F'],
    intensity: intensityForTier(9),
  },
  prismatic: {
    id: 'prismatic',
    label: 'Prismatic',
    tier: 10,
    paint: 'spectrum',
    colors: ['#FF1F5A', '#FF9F1C', '#FFE81F', '#3DFF7A', '#22E4DB', '#3B82F6', '#A855F7'],
    intensity: intensityForTier(10),
  },
});

/** CSS custom property name carrying a rarity colour stop. */
export function rarityColorVariable(rarity: Rarity, stop = 0): string {
  return `--rarity-${rarity}-${stop}`;
}

/* -------------------------------------------------------------------------- */
/*  Rarity subsets                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The gacha spans all ten rarities, but an individual **item category** does
 * not necessarily exist in all of them.
 *
 * `Rarity` is the rarity scale. It is NOT a statement that every item type has
 * a variant at every tier. Each category that is restricted declares its own
 * narrower type, and code that handles that category must use the narrow type
 * so an impossible combination cannot be constructed or persisted.
 *
 * Cannons are the first such category: they exist only in the top three tiers.
 */

/**
 * **The only rarities a cannon has.** Three, and a closed list.
 *
 * Common, Uncommon, Rare, Epic, Mythic and Legendary cannons **do not exist**
 * and never will — there is nothing pending about them. Neither does a
 * Prismatic one: it was a planned fourth cannon, and that update was
 * cancelled (ADR 0132). There is no "initial" and "future" split here any
 * more, because there is no future cannon for one to name.
 */
export const CANNON_RARITIES = [
  'excellent',
  'perfect',
  'anomaly',
] as const satisfies readonly Rarity[];

export type CannonRarity = (typeof CANNON_RARITIES)[number];

export const cannonRaritySchema = z.enum(CANNON_RARITIES);

export function isCannonRarity(value: string): value is CannonRarity {
  return (CANNON_RARITIES as readonly string[]).includes(value);
}

/** Narrows a general `Rarity` to a `CannonRarity`, throwing when impossible. */
export function assertCannonRarity(rarity: Rarity): CannonRarity {
  if (!isCannonRarity(rarity)) {
    throw new RangeError(
      `${rarity} is not a cannon rarity; cannons exist only as ${CANNON_RARITIES.join(', ')}`,
    );
  }
  return rarity;
}
